import { useRef, useEffect, useCallback, useMemo } from 'react';
import { ButtonGroup, Button, Menu, MenuContent, MenuTrigger, MenuItem } from '@cad-arc';
import { observer } from 'mobx-react-lite';
import { useWatch } from 'react-hook-form';
import { v4 } from 'uuid';
import {
    ConsolidatedLocationView,
    EventPanelFormType,
    ProQaSettingForAgencyType,
} from '@mark43/cad-api';
import { useApiClient } from '@m43/core';

import { useBroadcast } from '../../common/broadcast-channel/use-broadcast';
import { useIntegratedForm } from '../../forms/use-integrated-form';
import { globalAttributes } from '../../common/global-attributes';
import { useIntegratedActionBroadcaster } from '../../action-broadcaster/use-integrated-action-broadcaster';
import { useApplicationType } from '../../common/hooks/use-application-type';
import { usePhoneCallsStore } from '../../phone-calls/use-phone-calls';
import type { ProQaMessageTypeLegacyElectron } from '../../common/types';
import type { EventInfo } from '../../common/broadcast-channel/types';
import { useEventMetadataStore } from './event-metadata';
import {
    DISCIPLINE_LABEL_ENUM,
    DISCIPLINE_SELECTOR_SLUGS,
    PRO_QA_TYPES_MAP_FOR_LEGACY_ELECTRON,
} from './constants';
import {
    Discipline,
    buildEventInfo,
    buildOverwriteSignature,
    checkIsSavedEvent,
    decideUnackedNewCaseNoRetry,
    generateTempIncidentId,
    isTemporaryIncidentId,
    logMissingParameterWarning,
} from './helpers/pro-qa-buttons-helpers';
import {
    getResolvedTemporaryIncidentIds,
    ProQaTempIncidentStorageKeys,
} from './helpers/pro-qa-temp-incident-storage';
import { useBootstrapState } from '../../bootstrap/bootstrap-provider';
import { useProQaSessionTracker } from '../../pro-qa/use-pro-qa-session-tracker';
import { proQaResources } from '../pro-qa/pro-qa-resources';
import { useProQaSecondaryLaunchActionHandler } from './hooks/use-pro-qa-secondary-launch-action-handler';
import { ActionHandler } from '../../action-broadcaster/use-integrated-action-broadcaster';

type DisciplineSelectOptions = { isSecondarySession?: boolean };
type DisciplineSelectHandler = (
    discipline: Discipline,
    launchIncidentId?: string,
    options?: DisciplineSelectOptions
) => void;
type AgencyTypeKey = keyof typeof globalAttributes['agencyTypeGlobal'];
const PRO_QA_DISCIPLINES: Discipline[] = ['POLICE', 'FIRE', 'EMS'];

export const EventPanelFormOpenProQaButtons = observer(function EventPanelFormOpenProQaButtons({
    enabledDisciplines,
    enhancementsEnabled,
    temporaryIncidentIds,
    updateTemporaryIncidentId,
    proQaTempIncidentStorageKeys,
}: {
    enabledDisciplines: Discipline[];
    enhancementsEnabled?: boolean;
    temporaryIncidentIds: Record<Discipline, string | undefined>;
    updateTemporaryIncidentId: (discipline: Discipline, id: string | undefined) => void;
    proQaTempIncidentStorageKeys: ProQaTempIncidentStorageKeys;
}) {
    const { isElectronRewrite } = useApplicationType();
    const { proQaSettings } = useBootstrapState();
    const { postMessage } = useBroadcast('legacy-store-broadcast');
    const eventForm = useIntegratedForm<EventPanelFormType>().form;
    const eventMetadataStore = useEventMetadataStore();
    const eventMetadata = eventMetadataStore.state.metadata;
    const apiClient = useApiClient();
    const proQaResource = useMemo(() => proQaResources(apiClient), [apiClient]);
    const phoneCallsStore = usePhoneCallsStore();
    const eventId = eventMetadataStore.state.metadata?.eventId;
    const agencyEventNumber = eventMetadataStore.state.metadata?.agencyEventNumber;
    const isSavedEvent = checkIsSavedEvent(eventId, agencyEventNumber);
    const savedPhoneNumberId = eventMetadataStore.state.metadata?.eventPhoneCall?.phoneNumberId;
    const { checkIfSessionWasClosed } = useProQaSessionTracker(agencyEventNumber);

    const eventLocation = useWatch({
        control: eventForm.control,
        name: 'location',
    }) as ConsolidatedLocationView | null;

    const reportingPartyFirstName = useWatch({
        control: eventForm.control,
        name: 'reportingParties.0.firstName',
    });

    const reportingPartyLastName = useWatch({
        control: eventForm.control,
        name: 'reportingParties.0.lastName',
    });

    const reportingPartyPhoneNumber = useWatch({
        control: eventForm.control,
        name: 'reportingParties.0.phoneNumber',
    });

    const phoneCall = useWatch({
        control: eventForm.control,
        name: 'phoneCall',
    });

    const phoneNumberId = savedPhoneNumberId ?? phoneCall?.phoneNumberId;

    const phoneNumberInfo = phoneNumberId
        ? phoneCallsStore.getPhoneCallByPhoneNumberId(phoneNumberId)
        : undefined;

    const initiatedDateTimeUtc = useWatch({
        control: eventForm.control,
        name: 'initiatedDateTimeUtc',
    });

    const eventInfo = useMemo(
        () =>
            buildEventInfo({
                metadata: eventMetadataStore.state.metadata ?? {},
                location: eventLocation,
                reportingParty: {
                    firstName: reportingPartyFirstName,
                    lastName: reportingPartyLastName,
                    phoneNumber: reportingPartyPhoneNumber,
                },
                phoneCallDisplay: phoneNumberInfo?.normalizedPhoneCallDisplay,
                enhancementsEnabled,
                initiatedDateTimeUtc,
            }),
        [
            eventMetadataStore.state.metadata,
            eventLocation,
            reportingPartyFirstName,
            reportingPartyLastName,
            reportingPartyPhoneNumber,
            phoneNumberInfo?.normalizedPhoneCallDisplay,
            enhancementsEnabled,
            initiatedDateTimeUtc,
        ]
    );

    const temporaryIncidentIdsRef = useRef<Record<Discipline, string | undefined>>(
        temporaryIncidentIds
    );
    const failedOverwriteSignaturesRef = useRef<Partial<Record<Discipline, string>>>({});
    /**
     * Tracks (tempId -> savedId) signatures for OVERWRITE messages we have already
     * dispatched but haven't yet seen a confirming SUCCEEDED/FAILED for. Keeps the
     * effect from re-firing the same OVERWRITE on every re-render while ProQA is
     * still expected to ack.
     */
    const pendingOverwriteSignaturesRef = useRef<Partial<Record<Discipline, string>>>({});

    useEffect(() => {
        temporaryIncidentIdsRef.current = temporaryIncidentIds;
    }, [temporaryIncidentIds]);

    const setTempIncidentId = useCallback(
        (discipline: Discipline, id: string | undefined) => {
            updateTemporaryIncidentId(discipline, id);
            temporaryIncidentIdsRef.current = {
                ...temporaryIncidentIdsRef.current,
                [discipline]: id,
            };
        },
        [updateTemporaryIncidentId]
    );

    const handleIncidentNumberOverrideResult: ActionHandler<'EVENT_V2'> = useCallback(
        (action) => {
            if (
                action.type !== 'PRO_QA_INCIDENT_NUMBER_OVERRIDE_FAILED' &&
                action.type !== 'PRO_QA_INCIDENT_NUMBER_OVERRIDE_SUCCEEDED'
            ) {
                return false;
            }

            const discipline = action.payload.agencyType as Discipline;
            if (!PRO_QA_DISCIPLINES.includes(discipline)) {
                return false;
            }
            const savedEventNumber = eventMetadata?.agencyEventNumber?.trim();
            const previousIncidentNumber = String(action.payload.previousIncidentNumber);
            const attemptedIncidentNumber = String(action.payload.attemptedIncidentNumber);

            if (!savedEventNumber || savedEventNumber !== attemptedIncidentNumber) {
                return false;
            }

            // Always release the in-flight marker for this discipline so the effect can re-evaluate on subsequent renders if state changes.
            delete pendingOverwriteSignaturesRef.current[discipline];

            if (action.type === 'PRO_QA_INCIDENT_NUMBER_OVERRIDE_SUCCEEDED') {
                // ProQA confirmed the overwrite — safe to drop the temp id we kept alive as the recovery handle.
                setTempIncidentId(discipline, undefined);
                return true;
            }

            // FAILED: keep the temp id intact so a follow-up "Open ProQA" can resume
            // ProQA's original session, and remember the (temp -> saved) pair so the
            // effect doesn't auto-retry the same overwrite.
            const currentTempId = temporaryIncidentIdsRef.current[discipline];
            if (currentTempId && String(currentTempId) !== previousIncidentNumber) {
                return false;
            }

            failedOverwriteSignaturesRef.current[discipline] = buildOverwriteSignature(
                previousIncidentNumber,
                attemptedIncidentNumber
            );
            if (!currentTempId) {
                setTempIncidentId(discipline, previousIncidentNumber);
            }
            return true;
        },
        [eventMetadata?.agencyEventNumber, setTempIncidentId]
    );

    const { trigger } = useIntegratedActionBroadcaster({
        windowType: 'EVENT_V2',
        ignoreActiveWorkspaceWindowType: true,
        handler: handleIncidentNumberOverrideResult,
    });

    const openProQa = useCallback(
        (type: AgencyTypeKey, launchEventInfo: EventInfo) => {
            const proQaSettingsForAgency = (proQaSettings[
                globalAttributes.agencyTypeGlobal[type]
            ] as unknown) as ProQaSettingForAgencyType[] | undefined;
            const windowFocusConfig = enhancementsEnabled
                ? proQaSettingsForAgency?.[0]?.windowFocusHorizontal
                : undefined;
            const wasSessionClosed = checkIfSessionWasClosed(type);

            trigger([
                {
                    id: v4(),
                    targetWindowType: 'ROOT',
                    type: 'OPEN_PRO_QA',
                    payload: {
                        eventInfo: { ...launchEventInfo },
                        type,
                        wasSessionClosed,
                        ...(windowFocusConfig !== undefined
                            ? { windowFocusHorizontal: windowFocusConfig }
                            : {}),
                    },
                },
            ]);
        },
        [checkIfSessionWasClosed, enhancementsEnabled, proQaSettings, trigger]
    );

    const openProQALegacyElectron = useCallback(
        (type: ProQaMessageTypeLegacyElectron, launchEventInfo: EventInfo) => {
            postMessage({
                type: 'OPEN_PRO_QA',
                payload: {
                    eventInfo: { ...launchEventInfo },
                    panel: {
                        isCadNext: true,
                    },
                    type,
                },
            });
        },
        [postMessage]
    );

    const handleDisciplineSelect: DisciplineSelectHandler = useCallback(
        (discipline, launchIncidentId, options) => {
            if (!eventInfo.id && !enhancementsEnabled) {
                logMissingParameterWarning('event ID');
                return;
            }

            const trimmedLaunchIncidentId = launchIncidentId?.trim();
            const hasLaunchIncidentId = !!trimmedLaunchIncidentId;
            let resolvedIncidentId =
                trimmedLaunchIncidentId ?? temporaryIncidentIdsRef.current[discipline];
            let generatedTempIncidentId = false;
            const currentDiscipline = getDisciplineFromAgencyType(
                eventMetadata?.agencyTypeGlobalAttrId
            );
            // manual launches from the Open ProQA menu should stay bound to the current event,
            // only explicit secondary-launch actions should opt into `secondarySession`.
            const isSecondarySession = options?.isSecondarySession ?? false;

            if (hasLaunchIncidentId) {
                if (isTemporaryIncidentId(trimmedLaunchIncidentId)) {
                    setTempIncidentId(discipline, trimmedLaunchIncidentId);
                } else {
                    // secondary launches on saved events can provide agency event numbers,
                    // keep only generated temporary IDs in session state
                    setTempIncidentId(discipline, undefined);
                }
            }
            if (isSavedEvent && !hasLaunchIncidentId && !resolvedIncidentId) {
                const fallbackIncident = eventInfo.incidentNumber
                    ? String(eventInfo.incidentNumber).trim()
                    : undefined;
                if (fallbackIncident) {
                    resolvedIncidentId = fallbackIncident;
                    setTempIncidentId(discipline, undefined);
                }
            }

            if (!resolvedIncidentId) {
                resolvedIncidentId = generateTempIncidentId();
                setTempIncidentId(discipline, resolvedIncidentId);
                generatedTempIncidentId = true;
            }

            /**
             * Retry an unacked NEWCASENO migration on user-initiated click.
             * Caps retries to user clicks (no retry storm), skips when ProQA
             * already explicitly rejected or migrated, and only fires for the
             * exact (temp -> saved) pair we originally tried.
             */
            const retryAction = decideUnackedNewCaseNoRetry({
                discipline,
                tempIncidentId: temporaryIncidentIdsRef.current[discipline],
                savedEventNumber: agencyEventNumber?.trim(),
                pendingSignature: pendingOverwriteSignaturesRef.current[discipline],
            });
            if (retryAction) {
                trigger([
                    {
                        id: v4(),
                        targetWindowType: 'ROOT',
                        ...retryAction,
                    },
                ]);
            }

            const launchEventInfo = {
                ...eventInfo,
                incidentNumber: resolvedIncidentId,
                secondarySession: isSecondarySession || undefined,
            };

            if (isElectronRewrite) {
                openProQa(discipline, launchEventInfo);
            } else {
                openProQALegacyElectron(
                    PRO_QA_TYPES_MAP_FOR_LEGACY_ELECTRON[discipline],
                    launchEventInfo
                );
            }

            if (isSavedEvent && discipline !== currentDiscipline && generatedTempIncidentId) {
                setTempIncidentId(discipline, undefined);
            }
        },
        [
            enhancementsEnabled,
            eventInfo,
            eventMetadata?.agencyTypeGlobalAttrId,
            agencyEventNumber,
            isSavedEvent,
            isElectronRewrite,
            openProQa,
            openProQALegacyElectron,
            setTempIncidentId,
            trigger,
        ]
    );

    useProQaSecondaryLaunchActionHandler({
        enhancementsEnabled,
        eventId: eventMetadata?.eventId,
        agencyEventNumber: eventMetadata?.agencyEventNumber,
        temporaryIncidentIdsRef,
        handleDisciplineSelect,
        consumePendingSecondaryLaunches: proQaResource.consumePendingSecondaryLaunches,
    });

    useEffect(
        function overwriteTemporaryIncidentIdsWithSavedEventNumber() {
            const currentEventId = eventMetadata?.eventId;
            if (!currentEventId) {
                // not a saved event
                return;
            }

            const resolvedIncidentNumber = eventInfo.incidentNumber
                ? String(eventInfo.incidentNumber).trim()
                : undefined;
            if (!resolvedIncidentNumber) {
                return;
            }

            // ensure any persisted temp ids is reused (e.g. after reload) before considering new ones
            const storedTempIds = getResolvedTemporaryIncidentIds(
                proQaTempIncidentStorageKeys,
                temporaryIncidentIds
            );

            PRO_QA_DISCIPLINES.forEach((discipline) => {
                const tempId = storedTempIds?.[discipline]?.trim();
                if (!tempId) {
                    return;
                }

                if (!isTemporaryIncidentId(tempId)) {
                    setTempIncidentId(discipline, undefined);
                    return;
                }

                const signature = buildOverwriteSignature(tempId, resolvedIncidentNumber);

                // ProQA already rejected this exact temp -> saved mapping. Don't auto-retry
                if (failedOverwriteSignaturesRef.current[discipline] === signature) {
                    return;
                }
                delete failedOverwriteSignaturesRef.current[discipline];

                // OVERWRITE already in flight for the same pair — wait for the SUCCEEDED/FAILED ack before re-evaluating
                if (pendingOverwriteSignaturesRef.current[discipline] === signature) {
                    return;
                }
                pendingOverwriteSignaturesRef.current[discipline] = signature;

                trigger([
                    {
                        id: v4(),
                        targetWindowType: 'ROOT',
                        type: 'OVERWRITE_PRO_QA_INCIDENT_NUMBER',
                        payload: {
                            type: discipline,
                            incidentNumber: tempId,
                            overIncidentNumber: resolvedIncidentNumber,
                        },
                    },
                ]);

                // Keep the temp id in storage as the recovery handle. It is cleared
                // only when Electron broadcasts SUCCEEDED (ProQA acked the NEWCASENO).
                // On FAILED or silence, the temp id stays so a subsequent "Open ProQA"
                // resumes ProQA's existing session under the temp id.
            });
        },
        [
            eventInfo.incidentNumber,
            eventMetadata?.eventId,
            temporaryIncidentIds,
            trigger,
            setTempIncidentId,
            proQaTempIncidentStorageKeys,
        ]
    );

    if (!enhancementsEnabled) {
        return (
            <ButtonGroup>
                {enabledDisciplines.map((discipline) => (
                    <Button
                        variant="outline"
                        trailingVisual="OpenNewWindow"
                        key={discipline}
                        onClick={() => handleDisciplineSelect(discipline)}
                    >
                        {DISCIPLINE_LABEL_ENUM[discipline].initLabel} ProQA
                    </Button>
                ))}
            </ButtonGroup>
        );
    }

    if (enabledDisciplines.length === 1) {
        const discipline = enabledDisciplines[0];
        return (
            <Button variant="outline" onClick={() => handleDisciplineSelect(discipline)}>
                {DISCIPLINE_LABEL_ENUM[discipline].initLabel} ProQA
            </Button>
        );
    }

    return (
        <Menu>
            <MenuTrigger asChild>
                <Button variant="outline" trailingVisual="Open">
                    Open ProQA
                </Button>
            </MenuTrigger>
            <MenuContent>
                {enabledDisciplines.map((discipline) => {
                    const selector = `open-proqa-${DISCIPLINE_SELECTOR_SLUGS[discipline]}-option`;
                    return (
                        <MenuItem
                            key={discipline}
                            id={selector}
                            onClick={() => handleDisciplineSelect(discipline)}
                        >
                            {DISCIPLINE_LABEL_ENUM[discipline].enhancementLabel}
                        </MenuItem>
                    );
                })}
            </MenuContent>
        </Menu>
    );
});

function getDisciplineFromAgencyType(agencyTypeGlobalAttrId?: number): Discipline | undefined {
    const { agencyTypeGlobal } = globalAttributes;
    switch (agencyTypeGlobalAttrId) {
        case agencyTypeGlobal.POLICE:
            return 'POLICE';
        case agencyTypeGlobal.FIRE:
            return 'FIRE';
        case agencyTypeGlobal.EMS:
            return 'EMS';
        default:
            return undefined;
    }
}
